import { useState, useRef, useEffect } from "react";

import { useNative } from "../NativeContext";

import style from "../style/components/Buttons.module.css";

const BlockButton = ({
    type = "Primary",
    text,
    loading = false,
    disabled = false,
    onClick,
}: {
    type?: "Primary" | "Secondary" | "Default" | "Alert";
    text: string;
    loading?: boolean;
    disabled?: boolean;
    onClick: () => void;
}) => {
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--blockButton"],
                // color by type
                type === "Primary" && style["container--blockButton--primary"],
                type === "Secondary" &&
                    style["container--blockButton--secondary"],
                type === "Default" && style["container--blockButton--default"],
                type === "Alert" && style["container--blockButton--alert"],
                // disabled
                disabled && style["container--blockButton--disabled"],
                // click event
                clicked && style["container--blockButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={() => {
                    if (!disabled && !loading) onClick();
                }}
                disabled={disabled || loading}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--blockButton"],
                    clicked && style["button--blockButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

const FooterButton = ({
    type = "Primary",
    text,
    loading = false,
    disabled,
    onClick,
}: {
    type?: "Primary";
    text: string;
    loading?: boolean;
    disabled: boolean;
    onClick: () => void;
}) => {
    const { deviceType, keyboardHeight } = useNative();
    const container = useRef<HTMLDivElement | null>(null);
    const [resizeHeight, setResizeHeight] = useState<number>(0);
    const [clicked, setClicked] = useState<boolean>(false);

    useEffect(() => {
        if (deviceType == "android") return;
        const resizeHandler = (event: Event) =>
            setResizeHeight(
                window.innerHeight -
                    (event.currentTarget as VisualViewport)?.height
            );
        visualViewport &&
            visualViewport.addEventListener("resize", resizeHandler);
        return () =>
            visualViewport?.removeEventListener("resize", resizeHandler);
    }, []);

    useEffect(() => {
        container.current!.style.setProperty(
            "--bottom-margin",
            `${resizeHeight}px`
        );
    }, [resizeHeight]);

    useEffect(() => {
        if (deviceType != "android") return;
        container.current!.style.setProperty(
            "--bottom-margin",
            `${keyboardHeight}px`
        );
    }, [keyboardHeight]);

    return (
        <div
            ref={container}
            className={[
                style["container--footerButton"],
                // color by type
                type === "Primary" && style["container--footerButton--primary"],
                disabled && style["container--footerButton--disabled"],
                deviceType == "android" &&
                    keyboardHeight &&
                    style["container--footerButton--keyboard"],
                deviceType != "android" &&
                    resizeHeight &&
                    style["container--footerButton--keyboard"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--footerButton"],
                    clicked && style["button--footerButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

// const FooterButton = ({
//     type = "Primary",
//     text,
//     loading = false,
//     disabled,
//     onClick,
// }: {
//     type?: "Primary";
//     text: string;
//     loading?: boolean;
//     disabled: boolean;
//     onClick: () => void;
// }) => {
//     const container = useRef<HTMLDivElement | null>(null);
//     const [clicked, setClicked] = useState<boolean>(false);
//     const [resizeHeight, setResizeHeight] = useState<number>(0);

//     useEffect(() => {
//         const resizeHandler = (event: Event) => {
//             console.log(
//                 "resized: ",
//                 (event.currentTarget as VisualViewport)?.height
//             );
//             setResizeHeight(
//                 window.innerHeight -
//                     (event.currentTarget as VisualViewport)?.height
//             );
//         };
//         visualViewport &&
//             visualViewport.addEventListener("resize", resizeHandler);
//         return () =>
//             visualViewport?.removeEventListener("resize", resizeHandler);
//     }, []);

//     useEffect(() => {
//         console.log("resizeHeight: ", resizeHeight);
//         container.current!.style.setProperty(
//             "--bottom-margin",
//             `${resizeHeight}px`
//         );
//     }, [resizeHeight]);

//     return (
//         <div
//             ref={container}
//             className={[
//                 style["container--footerButton"],
//                 // color by type
//                 type === "Primary" && style["container--footerButton--primary"],
//                 disabled && style["container--footerButton--disabled"],
//                 resizeHeight && style["container--footerButton--keyboard"],
//             ].join(" ")}
//         >
//             <button
//                 onClick={onClick}
//                 // click events
//                 onMouseLeave={() => setClicked(false)}
//                 onMouseDown={() => setClicked(true)}
//                 onMouseUp={() => setClicked(false)}
//                 onTouchStart={() => setClicked(true)}
//                 onTouchEnd={() => setClicked(false)}
//                 className={[
//                     style["button--footerButton"],
//                     clicked && style["button--footerButton--clicked"],
//                     loading && style["loading--default"],
//                 ].join(" ")}
//             >
//                 {text}
//             </button>
//         </div>
//     );
// };

const ListButton = ({
    type = "Primary",
    text,
    loading = false,
    onClick,
}: {
    type?: "Primary" | "Secondary" | "Default";
    text: string;
    loading?: boolean;
    onClick: () => void;
}) => {
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--listButton"],
                // color by type
                type === "Primary" && style["container--listButton--primary"],
                type === "Secondary" &&
                    style["container--listButton--secondary"],
                type === "Default" && style["container--listButton--default"],
                // click event
                clicked && style["container--listButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--listButton"],
                    clicked && style["button--listButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

const TextButton = ({
    type = "Primary",
    size = "Default",
    text,
    onClick,
}: {
    type?: "Primary";
    size?: "Default";
    text: string;
    onClick: () => void;
}) => {
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--textButton"],
                // color by type
                type === "Primary" && style["container--textButton--primary"],
                // click event
                clicked && style["container--textButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--textButton"],
                    // size
                    size === "Default" && style["button--textButton--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

const ArrowButton = ({
    color = "Default",
    size = "Fill",
    text,
    onClick,
}: {
    color?: "Default";
    size?: "Fill" | "Inline";
    text: string;
    onClick: () => void;
}) => {
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--arrowButton"],
                // color
                color === "Default" && style["container--arrowButton--default"],
                // size
                size === "Fill" && style["container--arrowButton--fill"],
                // click event
                clicked && style["container--arrowButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={style["button--arrowButton"]}
            >
                {text}

                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="17"
                    viewBox="0 0 10 17"
                    fill="#666666"
                >
                    <path d="M9.99991 8.5C9.99991 8.73041 9.90774 8.93318 9.73263 9.1083L2.43309 16.2512C2.26719 16.4171 2.06443 16.5 1.82479 16.5C1.35475 16.5 0.986084 16.1406 0.986084 15.6613C0.986084 15.4217 1.07825 15.2189 1.22572 15.0622L7.93539 8.5L1.22572 1.93779C1.07825 1.78111 0.986084 1.56912 0.986084 1.33871C0.986084 0.859447 1.35475 0.5 1.82479 0.5C2.06443 0.5 2.26719 0.582949 2.43309 0.739631L9.73263 7.89171C9.90774 8.0576 9.99991 8.26959 9.99991 8.5Z" />
                </svg>
            </button>
        </div>
    );
};

const BlockButtonWithArrow = ({
    type = "Default",
    text,
    onClick,
}: {
    type?: "Default";
    text: string;
    onClick: () => void;
}) => {
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--blockButton-with-arrow"],
                // color by type
                type === "Default" &&
                    style["container--blockButton-with-arrow--default"],
                // click event
                clicked && style["container--blockButton-with-arrow--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // click events
                onMouseLeave={() => setClicked(false)}
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--blockButton-with-arrow"],
                    clicked && style["button--blockButton-with-arrow--clicked"],
                ].join(" ")}
            >
                {text}
                <svg
                    width="10"
                    height="16"
                    viewBox="0 0 10 16"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M10 8C10 8.23041 9.90784 8.43318 9.73272 8.6083L2.43318 15.7512C2.26728 15.9171 2.06452 16 1.82489 16C1.35484 16 0.986176 15.6406 0.986176 15.1613C0.986176 14.9217 1.07834 14.7189 1.22581 14.5622L7.93548 8L1.22581 1.43779C1.07834 1.28111 0.986176 1.06912 0.986176 0.83871C0.986176 0.359447 1.35484 0 1.82489 0C2.06452 0 2.26728 0.0829493 2.43318 0.239631L9.73272 7.39171C9.90784 7.5576 10 7.76959 10 8Z"
                        fill="#666666"
                    />
                </svg>
            </button>
        </div>
    );
};

export {
    BlockButton,
    FooterButton,
    ListButton,
    TextButton,
    ArrowButton,
    BlockButtonWithArrow,
};
